import { z } from 'zod'
import { Normalization } from '~/utils/normalization'
import { phone } from '~/services/api/contracts/phone'
import { PhoneVerdict } from '~/enums/PhoneVerdict'
import { PhoneNumber } from '~/utils/PhoneNumber'
import {
  CheckoutCheckoutRequestSchema,
  DeliveryContactTypeSchema,
  OrderTypeSchema
} from '~/services/api/generated'
import type { NoContext } from '~/services/api/contracts/NoContext'
import { fieldNormalizer } from '~/services/api/contracts/fieldNormalizer'

const BLANK_TO_NULL_OPTIONAL_KEYS = [
  'delivery_business_name',
  'delivery_suite',
  'delivery_contact_person',
  'special_instructions'
] as const

// A key absent from the raw input stays absent -- introducing it (even as null/undefined)
// would add a wire key callers never sent, breaking exact-body assertions downstream.
const normalize = fieldNormalizer({
  always: { trimmed: ['delivery_address'] },
  blankToNull: BLANK_TO_NULL_OPTIONAL_KEYS,
  enums: [
    { field: 'delivery_contact_type', options: DeliveryContactTypeSchema.options },
    { field: 'order_type', options: OrderTypeSchema.options }
  ],
  numericString: ['guest_count']
})

const PHONE_FIELDS = ['delivery_phone', 'delivery_contact_phone'] as const

type CheckoutRequestBody = z.output<typeof CheckoutCheckoutRequestSchema>

// A valid phone leaves the contract as national digits, the form the API receives; any other entry
// stays as typed so its own verdict is reported.
const withWirePhones = (input: unknown): unknown => {
  const value = normalize(input)
  if (!Normalization.isRecord(value)) return value
  const wired = PHONE_FIELDS.flatMap((field) => {
    const candidate = value[field]
    const isValid =
      typeof candidate === 'string' && PhoneNumber.parse(candidate).verdict === PhoneVerdict.VALID
    return isValid ? [[field, PhoneNumber.wire(candidate)] as const] : []
  })
  return { ...value, ...Object.fromEntries(wired) }
}

// The phone rule's issues for each present phone field, re-pathed onto that field.
const addPhoneIssues = (value: unknown, issues: z.RefinementCtx): void => {
  if (!Normalization.isRecord(value)) return
  for (const field of PHONE_FIELDS) {
    const candidate = value[field]
    const result =
      candidate === null || candidate === undefined ? undefined : phone.safeParse(candidate)
    if (result?.success === false) {
      for (const issue of result.error.issues) issues.addIssue({ ...issue, path: [field] })
    }
  }
}

export const CheckoutCheckout = (_context: NoContext): z.ZodType<CheckoutRequestBody> =>
  z.preprocess(withWirePhones, CheckoutCheckoutRequestSchema).superRefine(addPhoneIssues)
